/* eslint-disable i18next/no-literal-string */
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  useTheme,
  IconButton,
  Tooltip,
  Button,
  Chip,
  TextField,
  Divider,
  Paper,
  Alert
} from '@mui/material';
import { ContentCopy, Delete, Add, Edit, Check } from '@mui/icons-material';
import { tokens } from '../../theme';

const ColorBox = ({ color, label, colorKey }) => {
  const theme = useTheme();
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#000000';
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const textColor = getContrastColor(color);

  return (
    <Card 
      sx={{ 
        height: 150, 
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'scale(1.05)',
        }
      }}
      onClick={() => copyToClipboard(color)}
    >
      <Box
        sx={{
          height: '70%',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <Tooltip title="Click to copy color">
          <IconButton 
            sx={{ 
              color: textColor,
              opacity: 0.7,
              '&:hover': { opacity: 1 }
            }}
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <CardContent sx={{ height: '30%', p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
          {color}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ColorSection = ({ title, colors, prefix = '' }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(colors).map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return Object.entries(value).map(([subKey, subValue]) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={`${key}-${subKey}`}>
                <ColorBox 
                  color={subValue} 
                  label={`${prefix}${key}.${subKey}`}
                  colorKey={`${key}.${subKey}`}
                />
              </Grid>
            ));
          } else {
            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={key}>
                <ColorBox 
                  color={value} 
                  label={`${prefix}${key}`}
                  colorKey={key}
                />
              </Grid>
            );
          }
        })}
      </Grid>
    </Box>
  );
};

const ComponentShowcase = () => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Button Variants
      </Typography>
      
      {/* Contained Buttons */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Contained</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" color="default">Default</Button>
          <Button variant="contained" color="primary">Primary</Button>
          <Button variant="contained" color="secondary">Secondary</Button>
          <Button variant="contained" color="error">Error</Button>
          <Button variant="contained" color="warning">Warning</Button>
          <Button variant="contained" color="info">Info</Button>
          <Button variant="contained" color="success">Success</Button>
          <Button variant="contained" startIcon={<Add />}>With Icon</Button>
        </Box>
      </Paper>

      {/* Outlined Buttons */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Outlined</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" color="default">Default</Button>
          <Button variant="outlined" color="primary">Primary</Button>
          <Button variant="outlined" color="secondary">Secondary</Button>
          <Button variant="outlined" color="error">Error</Button>
          <Button variant="outlined" color="warning">Warning</Button>
          <Button variant="outlined" color="info">Info</Button>
          <Button variant="outlined" color="success">Success</Button>
          <Button variant="outlined" startIcon={<Edit />}>With Icon</Button>
        </Box>
      </Paper>

      {/* Text Buttons */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Text</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button color="default">Default</Button>
          <Button color="primary">Primary</Button>
          <Button color="secondary">Secondary</Button>
          <Button color="error">Error</Button>
          <Button color="warning">Warning</Button>
          <Button color="info">Info</Button>
          <Button color="success">Success</Button>
          <Button startIcon={<Delete />}>With Icon</Button>
        </Box>
      </Paper>

      {/* Chips */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
        Chip Variants
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Filled</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label="Default" color="default" />
          <Chip label="Primary" color="primary" />
          <Chip label="Secondary" color="secondary" />
          <Chip label="Error" color="error" />
          <Chip label="Warning" color="warning" />
          <Chip label="Info" color="info" />
          <Chip label="Success" color="success" />
          <Chip label="With Delete" color="primary" onDelete={() => {}} />
          <Chip label="With Icon" color="secondary" icon={<Check />} />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Outlined</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label="Default" variant="outlined" color="default" />
          <Chip label="Primary" variant="outlined" color="primary" />
          <Chip label="Secondary" variant="outlined" color="secondary" />
          <Chip label="Error" variant="outlined" color="error" />
          <Chip label="Warning" variant="outlined" color="warning" />
          <Chip label="Info" variant="outlined" color="info" />
          <Chip label="Success" variant="outlined" color="success" />
          <Chip label="With Delete" variant="outlined" color="primary" onDelete={() => {}} />
        </Box>
      </Paper>

      {/* Text Fields */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
        TextField States
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Default" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Filled" defaultValue="Sample text" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Error" error helperText="Error message" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Disabled" disabled fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="With helper" helperText="Helper text" fullWidth />
          </Grid>
        </Grid>
      </Paper>

      {/* Cards */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
        Card Variants
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Default Card</Typography>
              <Typography variant="body2">
                This is a default card with elevation shadow.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Outlined Card</Typography>
              <Typography variant="body2">
                This is an outlined card without shadow.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const ThemeColorShowcase = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(mode);

  // Extract MUI palette colors
  const paletteColors = {
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
    error: theme.palette.error,
    warning: theme.palette.warning,
    info: theme.palette.info,
    success: theme.palette.success,
  };

  const textColors = theme.palette.text;
  const backgroundColors = theme.palette.background;
  const greyColors = theme.palette.grey;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
        Theme Color & Component Showcase
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4, opacity: 0.8 }}>
        Current mode: <strong>{mode}</strong> • Click any color to copy its hex value
      </Typography>

      <Divider sx={{ mb: 4 }} />

      {/* Component Examples */}
      <ComponentShowcase />

      <Divider sx={{ mb: 4 }} />

      {/* Main Palette Colors */}
      <ColorSection 
        title="Primary Colors" 
        colors={paletteColors.primary}
        prefix="palette.primary."
      />
      
      <ColorSection 
        title="Secondary Colors" 
        colors={paletteColors.secondary}
        prefix="palette.secondary."
      />
      
      <ColorSection 
        title="Error Colors" 
        colors={paletteColors.error}
        prefix="palette.error."
      />
      
      <ColorSection 
        title="Warning Colors" 
        colors={paletteColors.warning}
        prefix="palette.warning."
      />
      
      <ColorSection 
        title="Info Colors" 
        colors={paletteColors.info}
        prefix="palette.info."
      />
      
      <ColorSection 
        title="Success Colors" 
        colors={paletteColors.success}
        prefix="palette.success."
      />

      {/* Text Colors */}
      <ColorSection 
        title="Text Colors" 
        colors={textColors}
        prefix="palette.text."
      />

      {/* Background Colors */}
      <ColorSection 
        title="Background Colors" 
        colors={backgroundColors}
        prefix="palette.background."
      />

      {/* Grey Scale */}
      <ColorSection 
        title="Grey Scale (MUI Palette)" 
        colors={greyColors}
        prefix="palette.grey."
      />

      <Divider sx={{ my: 4 }} />
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Token Colors
      </Typography>

      {/* Token Colors */}
      <ColorSection 
        title="Grey Tokens" 
        colors={colors.grey}
        prefix="tokens.grey."
      />

      <ColorSection 
        title="Primary Tokens" 
        colors={colors.primary}
        prefix="tokens.primary."
      />

      <ColorSection 
        title="Green Accent" 
        colors={colors.greenAccent}
        prefix="tokens.greenAccent."
      />

      <ColorSection 
        title="Red Accent" 
        colors={colors.redAccent}
        prefix="tokens.redAccent."
      />

      <ColorSection 
        title="Blue Accent" 
        colors={colors.blueAccent}
        prefix="tokens.blueAccent."
      />

      <ColorSection 
        title="Pink Accent" 
        colors={colors.pinkAccent}
        prefix="tokens.pinkAccent."
      />

      <ColorSection 
        title="Yellow Accent" 
        colors={colors.yellowAccent}
        prefix="tokens.yellowAccent."
      />

      {/* Additional Colors */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Additional Colors
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <ColorBox 
              color={theme.palette.divider} 
              label="divider"
              colorKey="divider"
            />
          </Grid>
          {theme.palette.action && (
            <>
              <Grid item xs={6} sm={4} md={3} lg={2}>
                <ColorBox 
                  color={theme.palette.action.active} 
                  label="action.active"
                  colorKey="action.active"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg={2}>
                <ColorBox 
                  color={theme.palette.action.disabled} 
                  label="action.disabled"
                  colorKey="action.disabled"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default ThemeColorShowcase;